import React, { useState } from 'react';
import MD from './MD';

// Audit v3: nur echte http(s)-URLs als Link rendern — ghost_url/revid_video_url
// stammen aus API-Antworten; ein kompromittierter Dienst dürfte kein
// javascript:-Schema einschleusen können.
const safeHttp = (u) => (u && /^https?:\/\//i.test(u) ? u : undefined);

/**
 * UX-Sprint: Vollbild-Detail einer Idee (Muster ReportViewer).
 * Ersetzt die Inline-Expansion der Kanban-Karte — dort quetschten sich bis zu
 * ~14.000 Zeichen (Gliederung+Entwurf+Faktencheck+Repurpose, teils auto-open)
 * in eine 260px-Spalte. Hier: klare Abschnitte (zu, bis man sie öffnet),
 * BESCHRIFTETE Buttons (touch-tauglich — Tooltips gibt es auf iPad nicht)
 * und klickbare Report-Quellen.
 */

const STATUS_META = {
  offen:       { label: 'Offen',      icon: '○' },
  ausgewaehlt: { label: 'Ausgewählt', icon: '★' },
  in_arbeit:   { label: 'In Arbeit',  icon: '✎' },
  entwurf:     { label: 'Entwurf',    icon: '📄' },
  verworfen:   { label: 'Verworfen',  icon: '✕' },
};
const STATUS_ORDER = ['offen', 'ausgewaehlt', 'in_arbeit', 'entwurf', 'verworfen'];

const REPURPOSE_CHANNELS = [
  { key: 'linkedin',   label: 'LinkedIn',     icon: '💼' },
  { key: 'newsletter', label: 'Newsletter',   icon: '✉' },
  { key: 'teaser',     label: 'Teaser',       icon: '⚡' },
  { key: 'video',      label: 'Video-Skript', icon: '🎬', noButton: true },
];

const fmtDate = iso => iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// "Report #112 (Bot 21: …)" → klickbare Referenz
const parseReportRef = (s) => {
  const m = /Report #(\d+)/.exec(s || '');
  return m ? parseInt(m[1], 10) : null;
};

export default function IdeaModal({
  idea, busy, onClose,
  setStatus, generateOutline, generateDraft, generateFactCheck, pushToGhost,
  generateRepurpose, generateVideo, researchGap, saveNotes, handleDelete,
  onDiscussIdea, onOpenReport, copyText,
}) {
  const [confirmDel, setConfirmDel] = useState(false);
  if (!idea) return null;
  const st = idea.status || 'offen';
  const anyGen = idea.outline_status === 'generating' || idea.draft_status === 'generating'
    || idea.fact_check_status === 'generating' || idea.repurpose_status === 'generating';

  const pipelineBtn = (cond, onClick, label, hint, running, done) => (
    <button
      className={`idea-modal-action ${cond && !done ? 'idea-modal-action-next' : ''}`}
      disabled={!!busy || running || anyGen}
      onClick={onClick}
      title={hint}
    >
      {running ? '⏳ ' : ''}{label}{done ? ' ✓' : ''}
    </button>
  );

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal idea-modal">
        <div className="modal-header">
          <span className="modal-title">{STATUS_META[st].icon} {idea.title}</span>
          <div className="modal-header-actions">
            {onDiscussIdea && (
              <button className="btn-outline" onClick={() => onDiscussIdea(idea)} title="Diese Idee in einem neuen Chat besprechen">◈ Besprechen</button>
            )}
            <button className="btn-outline" onClick={onClose} title="Detail schließen">✕ Schließen</button>
          </div>
        </div>
        <div className="modal-body idea-modal-body">

          {/* Überblick */}
          <div className="idea-modal-meta">
            {(idea.batch_id || '').startsWith('kombi') && <span className="idea-kombi-badge">⊕ Kombi</span>}
            {idea.hook && <p className="idea-modal-hook">{idea.hook}</p>}
            {idea.kuka2me_angle && <div className="idea-field"><strong>KUKA2ME-Winkel:</strong> {idea.kuka2me_angle}</div>}
            {idea.format_suggestion && <div className="idea-field"><strong>Format:</strong> {idea.format_suggestion}</div>}
            <div className="idea-meta-row">{fmtDate(idea.created_at)} · batch {idea.batch_id || '—'}</div>
          </div>

          {/* Pipeline — beschriftete Aktionen, nächster Schritt hervorgehoben */}
          <div className="idea-modal-section">
            <div className="idea-modal-section-title">Pipeline</div>
            <div className="idea-modal-actions">
              {pipelineBtn(!idea.outline, () => generateOutline(idea.id),
                '📝 Gliederung' + (idea.outline ? ' neu' : ' erstellen'),
                'Redaktions-Gliederung aus Idee + Source-Reports (1–3 min, lokal)',
                idea.outline_status === 'generating', !!idea.outline)}
              {idea.outline && pipelineBtn(!idea.draft, () => generateDraft(idea.id),
                '📄 Entwurf' + (idea.draft ? ' neu' : ' schreiben'),
                'Artikel-Rohentwurf aus der Gliederung (2–4 min, lokal)',
                idea.draft_status === 'generating', !!idea.draft)}
              {idea.draft && pipelineBtn(!idea.fact_check, () => generateFactCheck(idea.id),
                '🔬 Faktencheck' + (idea.fact_check ? ' neu' : ''),
                'Entwurf gegen das Quellmaterial prüfen (1–3 min, lokal)',
                idea.fact_check_status === 'generating', !!idea.fact_check)}
              {idea.draft && !idea.ghost_post_id && pipelineBtn(!!idea.fact_check, () => pushToGhost(idea.id),
                '👻 Nach Ghost',
                'Als DRAFT ins Ghost-CMS — veröffentlicht wird nur manuell',
                idea.ghost_status === 'pushing', false)}
            </div>
            {idea.outline_status === 'error' && <div className="idea-outline-error">⚠ Gliederung fehlgeschlagen — nochmal versuchen.</div>}
            {idea.draft_status === 'error' && <div className="idea-outline-error">⚠ Entwurf fehlgeschlagen — nochmal versuchen.</div>}
            {idea.fact_check_status === 'error' && <div className="idea-outline-error">⚠ Faktencheck fehlgeschlagen.</div>}
          </div>

          {/* Verteilen */}
          {idea.draft && (
            <div className="idea-modal-section">
              <div className="idea-modal-section-title">Verteilen — du kopierst &amp; postest selbst</div>
              <div className="idea-modal-actions">
                {REPURPOSE_CHANNELS.filter(c => !c.noButton).map(c => (
                  <button key={c.key} className="idea-modal-action"
                    disabled={!!busy || idea.repurpose_status === 'generating'}
                    onClick={() => generateRepurpose(idea.id, c.key)}
                    title={`Entwurf als ${c.label} formulieren (lokal, ~1 min)`}>
                    {c.icon} {c.label}{(idea.repurpose || {})[c.key] ? ' ✓' : ''}
                  </button>
                ))}
                <button className="idea-modal-action"
                  disabled={!!busy || idea.repurpose_status === 'generating' || idea.revid_status === 'rendering'}
                  onClick={() => generateVideo(idea.id)}
                  title={idea.revid_video_url ? 'Video erneut erzeugen (kostet revid-Credits)' : 'Sprecher-Skript + Kurzvideo via revid.ai (kostet Credits, kein Auto-Publish)'}>
                  {idea.revid_status === 'rendering' ? '⏳' : '🎬'} Video{idea.revid_video_url ? ' ✓' : ''}
                </button>
              </div>
              {idea.repurpose_status === 'generating' && <div className="idea-outline-pending">♻ Variante wird erstellt…</div>}
              {idea.repurpose_status === 'error' && <div className="idea-outline-error">⚠ Repurpose fehlgeschlagen — nochmal versuchen.</div>}
              {idea.revid_status === 'rendering' && <div className="idea-outline-pending">🎬 Video rendert bei revid.ai (einige Minuten, Cloud)…</div>}
              {idea.revid_status === 'error' && <div className="idea-outline-error">⚠ Video-Render fehlgeschlagen — erneut klicken (resumed, kein Doppel-Credit).</div>}
              {idea.ghost_status === 'error' && <div className="idea-outline-error">⚠ Ghost-Push fehlgeschlagen — „👻 Nach Ghost" erneut versuchen.</div>}
              {idea.ghost_url && (
                <div className="idea-ghost-link">👻 <a href={safeHttp(idea.ghost_url)} target="_blank" rel="noopener noreferrer">Ghost-Entwurf öffnen</a><span className="idea-ghost-ok"> · Draft, nicht veröffentlicht</span></div>
              )}
              {idea.revid_video_url && (
                <div className="idea-ghost-link">🎬 <a href={safeHttp(idea.revid_video_url)} target="_blank" rel="noopener noreferrer">Video ansehen (revid.ai)</a><span className="idea-ghost-ok"> · sichten + Art.-50-Kennzeichnung vor Veröffentlichung</span></div>
              )}
            </div>
          )}

          {/* Quellen — klickbare Report-Refs + URLs */}
          {(Array.isArray(idea.synthesis_from) && idea.synthesis_from.length > 0) || (Array.isArray(idea.source_urls) && idea.source_urls.length > 0) ? (
            <div className="idea-modal-section">
              <div className="idea-modal-section-title">Quellen</div>
              {Array.isArray(idea.synthesis_from) && idea.synthesis_from.map((s, i) => {
                const rid = parseReportRef(s);
                return rid ? (
                  <button key={i} className="idea-report-ref" onClick={() => onOpenReport(rid)}
                    title={`Report #${rid} öffnen`}>📑 {s}</button>
                ) : (
                  <div key={i} className="idea-field">{s}</div>
                );
              })}
              {Array.isArray(idea.source_urls) && idea.source_urls.length > 0 && (
                <ul className="idea-sources">
                  {idea.source_urls.map((u, i) => (
                    <li key={i}><a href={u.startsWith('http') ? u : `https://${u}`} target="_blank" rel="noopener noreferrer">{u}</a></li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {/* Lücken → Loop-Closure */}
          {idea.gaps && (
            <div className="idea-modal-section">
              <div className="idea-modal-section-title">Offene Lücken</div>
              <div className="idea-field">{idea.gaps}</div>
              <button className="gap-research-btn" disabled={!!busy}
                onClick={() => researchGap(idea.id)}
                title="Aus dieser Lücke einen gezielten, einmaligen Recherche-Job erzeugen">
                🔍 Lücke recherchieren
              </button>
            </div>
          )}

          {/* Inhalte — bewusst ZU, bis man sie öffnet */}
          {idea.outline && (
            <details className="idea-outline">
              <summary>📋 Redaktions-Gliederung</summary>
              <div className="idea-outline-body"><MD content={idea.outline} /></div>
            </details>
          )}
          {idea.draft && (
            <details className="idea-outline idea-draft">
              <summary>📄 Artikel-Entwurf (Rohfassung)</summary>
              <div className="idea-outline-body"><MD content={idea.draft} /></div>
            </details>
          )}
          {idea.fact_check && (
            <details className="idea-outline idea-factcheck">
              <summary>🔍 Faktencheck</summary>
              <div className="idea-outline-body"><MD content={idea.fact_check} /></div>
            </details>
          )}
          {Array.isArray(idea.fact_check_needs) && idea.fact_check_needs.length > 0 && (
            <div className="factcheck-needs">
              <div className="factcheck-needs-label">🔍 Offene Punkte aus dem Faktencheck — gezielt nachrecherchieren:</div>
              {idea.fact_check_needs.map((nd, i) => (
                <div key={i} className="factcheck-need">
                  <span className="factcheck-need-text">{nd}</span>
                  <button className="gap-research-btn" disabled={!!busy}
                    onClick={() => researchGap(idea.id, nd)}
                    title="Diesen offenen Punkt als gezielten Recherche-Job starten">🔍</button>
                </div>
              ))}
            </div>
          )}
          {idea.repurpose && Object.keys(idea.repurpose).length > 0 && (
            <div className="repurpose-results">
              {REPURPOSE_CHANNELS.filter(c => (idea.repurpose || {})[c.key]).map(c => (
                <details key={c.key} className="idea-outline">
                  <summary>{c.icon} {c.label}</summary>
                  <div className="repurpose-result-head-modal">
                    <button className="btn-ghost-sm" onClick={() => copyText(idea.repurpose[c.key])} title="Text in die Zwischenablage kopieren">⧉ Kopieren</button>
                  </div>
                  <pre className="repurpose-result-text">{idea.repurpose[c.key]}</pre>
                </details>
              ))}
            </div>
          )}

          {/* Notizen */}
          <div className="idea-modal-section">
            <div className="idea-modal-section-title">Notizen</div>
            <textarea
              className="idea-notes" rows={3}
              placeholder="Notizen…"
              defaultValue={idea.notes || ''}
              onBlur={e => { if (e.target.value !== (idea.notes || '')) saveNotes(idea.id, e.target.value); }}
            />
          </div>

          {/* Status + Löschen — beschriftet, ganz unten */}
          <div className="idea-modal-section">
            <div className="idea-modal-section-title">Status</div>
            <div className="idea-modal-actions">
              {STATUS_ORDER.filter(s => s !== st).map(s => (
                <button key={s} className="idea-modal-action" disabled={!!busy}
                  onClick={() => setStatus(idea.id, s)}
                  title={`Status auf „${STATUS_META[s].label}" setzen`}>
                  {STATUS_META[s].icon} → {STATUS_META[s].label}
                </button>
              ))}
              {confirmDel ? (
                <>
                  <button className="idea-modal-action idea-modal-action-danger" disabled={!!busy}
                    onClick={() => { handleDelete(idea.id); onClose(); }}
                    title="Ja — Idee samt Gliederung, Entwurf und Faktencheck endgültig löschen">
                    🗑 Wirklich löschen?
                  </button>
                  <button className="idea-modal-action" onClick={() => setConfirmDel(false)}
                    title="Nicht löschen">Abbrechen</button>
                </>
              ) : (
                <button className="idea-modal-action idea-modal-action-danger" disabled={!!busy}
                  onClick={() => setConfirmDel(true)}
                  title="Idee löschen (mit Rückfrage — Gliederung/Entwurf/Faktencheck gehen mit verloren)">
                  🗑 Löschen
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
